<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { ArrowLeft, X } from 'lucide-vue-next'
import ScrollTop from 'primevue/scrolltop'
import { useDynamicModalStore } from '@/stores/dynamicModalStore'
import CloseButton from '@/components/utils/CloseButton.vue'

const Shortcuts = defineAsyncComponent(() => import('@/components/dynamic_modal/DynamicModalShortcuts.vue'))

const dynamicModalStore = useDynamicModalStore()
const { hidden, title, subtitle, activeComponents, activeProps, activeIndex, icon, theme, shortcuts } = storeToRefs(dynamicModalStore)

const modalcontent = ref<HTMLElement | null>(null)

function playAnimation() {
	if (modalcontent.value) {
		modalcontent.value.classList.remove('animate')
		void modalcontent.value.offsetWidth
		modalcontent.value.classList.add('animate')
	}
}

watch(activeIndex, () => {
	playAnimation()
})

const closable = computed(() => {
	return activeComponents.value[activeIndex.value].data.closable
})
</script>

<template>
	<div
		v-if="activeIndex >= 0"
		class="fixed bg-slate-950/50 left-0 top-0 h-[100dvh] w-screen z-[16] backdrop-blur-sm"
		@click="closable ? dynamicModalStore.closeModal() : null"
	/>
	<div
		v-if="!hidden"
		class="animate-modal"
		:class="`navigable-modal${theme}`"
	>
		<div class="header">
			<Button
				:class="!activeIndex ? '!hidden' : ''"
				class="button-purple-shade !px-2 back-button absolute top-2 left-2"
				outlined
				@click="dynamicModalStore.restorePreviousComponent"
			>
				<template #default>
					<ArrowLeft class="lucide-button-icon" />
				</template>
			</Button>
			<div class="flex-center flex-col title">
				<div class="text-h6 font-bold flex-center gap-2">
					<img
						v-if="typeof icon === 'string'"
						:src="icon"
						alt="icon"
						class="w-8 bg-orange-400 rounded-full p-1"
					>
					<component
						:is="icon"
						v-else
						class="lucide-button-icon-sm"
					/>
					{{ title }}
				</div>
				<div
					v-if="subtitle && subtitle.length"
					class="font-medium text-xs"
				>
					{{ subtitle }}
				</div>
			</div>
			<div
				class="exit-button absolute top-2 right-2"
				:class="shortcuts?.length ? 'flex flex-nowrap gap-1' : ''"
			>
				<Shortcuts
					v-if="shortcuts?.length"
					:shortcuts="shortcuts"
				/>
				<CloseButton
					v-if="closable"
					@click="dynamicModalStore.closeModal"
				/>
			</div>
		</div>
		<div
			v-if="activeComponents[activeIndex]"
			ref="modalcontent"
			:class="`modal-content${theme}`"
		>
			<keep-alive>
				<component
					:is="activeComponents[activeIndex].component"
					:key="activeComponents[activeIndex].type"
					v-bind="activeProps[activeIndex]"
				/>
			</keep-alive>

			<ScrollTop
				target="parent"
				:threshold="500"
				icon="pi pi-arrow-up"
				:pt="{
					root: 'z-[17] fixed rounded-full p-3 bg-slate-600 shadow-md shadow-slate-950/80 hover:bg-slate-500 duration-300 bottom-20 hover:-translate-y-1 duration-300 !transition-all',
					icon: {
						class: 'text-base',
					},
				}"
			/>
		</div>
		<div class="absolute pointer-events-none -top-10 -right-10 w-40 h-40 bg-blue-500/10 rounded-full blur-xl" />
		<div class="absolute pointer-events-none -bottom-10 -left-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl" />
	</div>
</template>

<style scoped lang="scss">
.navigable-modal-borderless {
	@apply fixed z-[17] flex flex-col shadow-md shadow-slate-950 overflow-hidden;

	@apply min-w-[300px] max-w-[400px] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md;

	& > .header {
		@apply z-[1];
		& > .title,
		& > .back-button {
			@apply hidden;
		}
	}

}

.modal-content-borderless {
	@apply w-full flex justify-center overflow-hidden overflow-y-auto m-auto h-fit;
}

.navigable-modal {
  @apply md:rounded-2xl;
  /* Fullscreen */
  @apply top-0 left-0 w-full h-full;

  /* Windowed*/
  @apply md:max-h-[75%] md:h-fit md:min-w-[30%] md:max-w-[500px] md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-md;

}

.navigable-modal-fs {
  /* Fullscreen */
  @apply top-0 left-0 w-full h-full;
}

.navigable-modal-large {
	@apply md:rounded-2xl;
	/* Fullscreen */
	@apply top-0 left-0 w-full h-full;

	/* Windowed*/
	@apply md:h-[80%] md:w-[80%] md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-md;
}

.navigable-modal,
.navigable-modal-fs,
.navigable-modal-large {
	@apply fixed z-[17] flex flex-col overflow-hidden;
	@apply shadow-2xl shadow-slate-950 bg-gradient-to-b from-slate-900 to-slate-800;
	& > .header {
    @apply w-full flex py-1 justify-center items-center px-3
      md:rounded-t-md border-0 border-b-[1px] border-b-slate-700 border-solid min-h-[50px] max-h-[50px];
  }
}

.modal-content,
.modal-content-fs,
.modal-content-large {
	@apply relative !h-full w-full flex flex-col overflow-hidden;
	& > *:first-child {
		@apply overflow-y-auto max-h-full m-auto py-4;
	}
}

.button {
    @apply outline-none rounded-full border-[1px] border-orange-400 p-2 bg-orange-400/20 hover:bg-orange-400/30;
    &:hover {
        box-shadow: 0 0 8px 1px rgba(249, 115, 22, 0.6);
    }
}

.modal-content.animate,
.modal-content-fs.animate,
.modal-content-borderless.animate {
  animation: fade-slide 0.3s ease;
}

@keyframes fade-slide {
  from {
    opacity: 0;
    transform: translateX(10%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.navigable-modal.animate-modal,
.navigable-modal-fs.animate-modal,
.navigable-modal-borderless.animate-modal {
  animation: slide-modal 0.3s ease;
}

@keyframes fade-slide {
  from {
	@apply translate-x-[10%];
    opacity: 0;
  }
  to {
	@apply translate-x-[0%];
    opacity: 1;
  }
}

@keyframes slide-modal {
  from {
	@apply translate-x-[-100%];
    opacity: 0;
  }
  to {
	@apply md:translate-x-[-50%] md:translate-y-[-50%];
    opacity: 1;
  }
}
</style>
